import { useState, useEffect } from "react";
import { MessageCircle, Send, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { createOrder, getProducts } from "@/lib/api";

const WHATSAPP_NUMBER = "19146730325";

export default function ContactPage() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    message: "",
    products: [],
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getProducts({}).then((res) => setProducts(res.data)).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleProductToggle = (productName) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.includes(productName)
        ? prev.products.filter((p) => p !== productName)
        : [...prev.products, productName],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_phone) {
      toast.error("Por favor completa nombre y teléfono");
      return;
    }
    setSubmitting(true);
    try {
      await createOrder(form);
      toast.success("Pedido enviado correctamente. Te contactaremos pronto.");
      setForm({ customer_name: "", customer_email: "", customer_phone: "", message: "", products: [] });
    } catch (e) {
      toast.error("Error al enviar pedido. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappMsg = encodeURIComponent(
    `Hola Oymer, soy ${form.customer_name || "[tu nombre]"}. Quiero hacer un pedido de: ${form.products.length > 0 ? form.products.join(", ") : "[productos]"}. ${form.message || ""}`
  );

  return (
    <div data-testid="contact-page" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <span className="font-body text-xs font-semibold tracking-[0.2em] uppercase text-terracotta">Contacto</span>
          <h1 className="font-heading text-5xl md:text-6xl tracking-tighter font-medium text-forest mt-2">
            Haz tu pedido
          </h1>
          <p className="font-body text-base md:text-lg text-sage mt-3 max-w-xl">
            Completa el formulario o contáctanos directamente por WhatsApp. Te responderemos lo antes posible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} data-testid="order-form" className="bg-white rounded-[2rem] p-8 md:p-12 border border-[#E5E0D8] shadow-[0_8px_30px_rgb(42,74,60,0.04)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block font-body text-sm font-medium text-forest mb-2">Nombre *</label>
                  <input
                    type="text"
                    name="customer_name"
                    data-testid="input-customer-name"
                    value={form.customer_name}
                    onChange={handleChange}
                    placeholder="Tu nombre completo"
                    className="w-full rounded-xl border border-[#E5E0D8] bg-white/50 px-4 py-3 font-body text-sm text-forest transition-colors focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 placeholder:text-sage"
                    required
                  />
                </div>
                <div>
                  <label className="block font-body text-sm font-medium text-forest mb-2">Teléfono *</label>
                  <input
                    type="tel"
                    name="customer_phone"
                    data-testid="input-customer-phone"
                    value={form.customer_phone}
                    onChange={handleChange}
                    placeholder="+52 55 1234 5678"
                    className="w-full rounded-xl border border-[#E5E0D8] bg-white/50 px-4 py-3 font-body text-sm text-forest transition-colors focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 placeholder:text-sage"
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block font-body text-sm font-medium text-forest mb-2">Email</label>
                <input
                  type="email"
                  name="customer_email"
                  data-testid="input-customer-email"
                  value={form.customer_email}
                  onChange={handleChange}
                  placeholder="tu@email.com (opcional)"
                  className="w-full rounded-xl border border-[#E5E0D8] bg-white/50 px-4 py-3 font-body text-sm text-forest transition-colors focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 placeholder:text-sage"
                />
              </div>

              {/* Product Selection */}
              {products.length > 0 && (
                <div className="mb-6">
                  <label className="block font-body text-sm font-medium text-forest mb-3">Productos de interés</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {products.map((p) => (
                      <button
                        type="button"
                        key={p.id}
                        data-testid={`product-select-${p.id}`}
                        onClick={() => handleProductToggle(p.name)}
                        className={`rounded-xl px-3 py-2 text-xs font-body font-medium border transition-all duration-200 text-left ${
                          form.products.includes(p.name)
                            ? "bg-forest text-white border-forest"
                            : "bg-white text-sage border-[#E5E0D8] hover:border-forest hover:text-forest"
                        }`}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-8">
                <label className="block font-body text-sm font-medium text-forest mb-2">Mensaje</label>
                <textarea
                  name="message"
                  data-testid="input-message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Cuéntanos qué necesitas..."
                  className="w-full rounded-xl border border-[#E5E0D8] bg-white/50 px-4 py-3 font-body text-sm text-forest transition-colors focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 placeholder:text-sage resize-none"
                />
              </div>

              <div className="flex flex-wrap gap-4">
                <button
                  type="submit"
                  data-testid="submit-order-button"
                  disabled={submitting}
                  className="bg-forest text-white rounded-full px-8 py-4 font-body font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_20px_rgba(42,74,60,0.15)] disabled:opacity-50 disabled:translate-y-0 flex items-center gap-2"
                >
                  <Send size={18} /> {submitting ? "Enviando..." : "Enviar Pedido"}
                </button>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMsg}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="bg-[#25D366] text-white rounded-full px-6 py-4 font-body font-medium flex items-center gap-2 transition-transform duration-300 hover:scale-105 shadow-[0_8px_20px_rgba(37,211,102,0.25)]"
                >
                  <MessageCircle size={20} strokeWidth={1.5} /> Pedir por WhatsApp
                </a>
              </div>
            </form>
          </div>

          {/* Contact Info Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-[2rem] p-8 border border-[#E5E0D8] shadow-[0_8px_30px_rgb(42,74,60,0.04)]">
              <h3 className="font-heading text-xl font-semibold text-forest mb-6">Información de Contacto</h3>
              <div className="space-y-5">
                <a href="https://wa.me/19146730325" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 group">
                  <div className="w-10 h-10 rounded-xl bg-forest/5 flex items-center justify-center flex-shrink-0 group-hover:bg-forest/10 transition-colors duration-200">
                    <Phone size={18} className="text-forest" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-body text-xs text-sage">WhatsApp</p>
                    <p className="font-body text-sm font-medium text-forest">+1 (914) 673-0325</p>
                  </div>
                </a>
                <a href="mailto:oymer1991@icloud.com" className="flex items-start gap-3 group">
                  <div className="w-10 h-10 rounded-xl bg-forest/5 flex items-center justify-center flex-shrink-0 group-hover:bg-forest/10 transition-colors duration-200">
                    <Mail size={18} className="text-forest" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-body text-xs text-sage">Email</p>
                    <p className="font-body text-sm font-medium text-forest">oymer1991@icloud.com</p>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-forest/5 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-forest" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-body text-xs text-sage">Ubicación</p>
                    <p className="font-body text-sm font-medium text-forest">Estados Unidos</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-forest rounded-[2rem] p-8 text-white">
              <h3 className="font-heading text-xl font-semibold mb-3">Respuesta rápida</h3>
              <p className="font-body text-sm text-white/70 leading-relaxed mb-6">
                Para una atención más rápida y personalizada, contáctanos directamente por WhatsApp.
              </p>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hola Oymer, quiero información sobre productos Vida Divina")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sidebar-whatsapp-button"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-5 py-3 font-body font-medium text-sm transition-transform duration-300 hover:scale-105"
              >
                <MessageCircle size={18} /> Abrir WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
